import { useEffect, useRef } from "react";
import { PD } from "@/constants/testIds";
import { sanitizeRtfHtml } from "@/lib/pageDesignerRtf";
import { registerRtfEditor } from "@/lib/pageDesignerRtfEditing";

export function PageDesignerRtfPreview({ html, testId }) {
  return (
    <div
      className="epd__rtf-preview"
      data-testid={testId}
      dangerouslySetInnerHTML={{ __html: sanitizeRtfHtml(html) }}
    />
  );
}

export default function PageDesignerRtfEditor({ cardId, html = "", disabled = false, onChange }) {
  const editorRef = useRef(null);
  const lastHtml = useRef("");
  const rangeRef = useRef(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    const next = html || "";
    if (next !== lastHtml.current && el.innerHTML !== next) {
      el.innerHTML = next;
      lastHtml.current = next;
    }
  }, [html]);

  useEffect(() => {
    const el = editorRef.current;
    if (!el || disabled) return undefined;

    const emitHtml = () => {
      const clean = sanitizeRtfHtml(el.innerHTML || "");
      lastHtml.current = clean;
      onChangeRef.current?.(clean);
    };

    const saveRange = () => {
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) return;
      const range = selection.getRangeAt(0);
      if (el.contains(range.commonAncestorContainer)) {
        rangeRef.current = range.cloneRange();
      }
    };

    const restoreRange = () => {
      el.focus();
      const saved = rangeRef.current;
      if (!saved) return;
      try {
        const selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(saved);
      } catch {
        /* selection may be stale after a card remount */
      }
    };

    const unregister = registerRtfEditor(cardId, {
      run(command, arg) {
        restoreRange();
        document.execCommand(command, false, arg);
        emitHtml();
        saveRange();
      },
    });

    el.addEventListener("keyup", saveRange);
    el.addEventListener("mouseup", saveRange);
    el.addEventListener("blur", saveRange);
    return () => {
      el.removeEventListener("keyup", saveRange);
      el.removeEventListener("mouseup", saveRange);
      el.removeEventListener("blur", saveRange);
      unregister();
    };
  }, [cardId, disabled]);

  const emitHtml = () => {
    const raw = editorRef.current?.innerHTML || "";
    const clean = sanitizeRtfHtml(raw);
    lastHtml.current = clean;
    onChange?.(clean);
  };

  return (
    <div className="epd__rtf" data-testid={`${PD.rtf}-${cardId}`}>
      <div
        ref={editorRef}
        className="epd__rtf-surface"
        data-testid={`${PD.rtfEditor}-${cardId}`}
        contentEditable={!disabled}
        suppressContentEditableWarning
        role="textbox"
        aria-multiline="true"
        aria-label="RTF text"
        spellCheck={false}
        data-empty-hint="Type text…"
        onInput={emitHtml}
        onBlur={emitHtml}
      />
    </div>
  );
}
